import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { KanbanEtapa, CreateKanbanEtapaRequest } from '@/types';
import { toast } from 'react-hot-toast';

interface KanbanEtapaModalProps {
  isOpen: boolean;
  onClose: () => void;
  etapa?: KanbanEtapa | null;
  kanbanId?: string;
  onSave: (etapa: KanbanEtapa) => void;
  isEditing?: boolean;
}

export const KanbanEtapaModal: React.FC<KanbanEtapaModalProps> = ({
  isOpen,
  onClose,
  etapa,
  kanbanId,
  onSave,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState<CreateKanbanEtapaRequest>({
    kanbanId: kanbanId || '',
    nome: '',
    descricao: '',
    cor: '#3b82f6',
    ordem: 1,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (etapa && isEditing) {
      setFormData({
        kanbanId: etapa.kanbanId,
        nome: etapa.nome,
        descricao: etapa.descricao || '',
        cor: etapa.cor,
        ordem: etapa.ordem,
      });
    } else {
      setFormData({
        kanbanId: kanbanId || '',
        nome: '',
        descricao: '',
        cor: '#3b82f6',
        ordem: 1,
      });
    }
  }, [etapa, isEditing, kanbanId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validação básica
      if (!formData.nome.trim()) {
        toast.error('Nome é obrigatório');
        return;
      }

      if (formData.ordem < 1) {
        toast.error('Ordem deve ser um número positivo');
        return;
      }

      // Simular salvamento (será implementado quando integrar com o backend)
      const savedEtapa: KanbanEtapa = {
        id: etapa?.id || `etapa-${Date.now()}`,
        kanbanId: formData.kanbanId,
        nome: formData.nome,
        descricao: formData.descricao || null,
        cor: formData.cor || '#3b82f6',
        ordem: formData.ordem,
        isAtivo: true,
        createdAt: etapa?.createdAt || new Date(),
        updatedAt: new Date(),
        _count: { titulos: 0 },
      };

      onSave(savedEtapa);
      toast.success(`Etapa ${isEditing ? 'atualizada' : 'criada'} com sucesso!`);
      onClose();
    } catch (error) {
      toast.error('Erro ao salvar etapa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Editar Etapa' : 'Nova Etapa'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="nome" className="block text-sm font-medium text-foreground text-foreground mb-1">
            Nome *
          </label>
          <Input
            id="nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            placeholder="Digite o nome da etapa"
            required
          />
        </div>

        <div>
          <label htmlFor="descricao" className="block text-sm font-medium text-foreground text-foreground mb-1">
            Descrição
          </label>
          <Textarea
            id="descricao"
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            placeholder="Digite uma descrição (opcional)"
            rows={3}
          />
        </div>

        <div>
          <label htmlFor="ordem" className="block text-sm font-medium text-foreground text-foreground mb-1">
            Ordem *
          </label>
          <Input
            id="ordem"
            type="number"
            min="1"
            value={formData.ordem}
            onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 1 })}
            placeholder="1"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground text-foreground mb-2">
            Cor
          </label>
          <ColorPicker
            value={formData.cor || '#3b82f6'}
            onChange={(color) => setFormData({ ...formData, cor: color })}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
